import React, { useEffect, useState } from 'react';
import axios from 'axios';
import defaultProfilePicture from '../Images/logo.png';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import config from '../config';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const ALL_AGENTS_SUMMARY = { username: 'All Agents', role: 'Summary' };

const COLORS = {
  cyan: '#00FFFF',
  red: '#FF4136',
  yellow: '#FFDC00',
  green: '#2ECC40',
  blue: '#0074D9',
  purple: '#B10DC9',
};

const statusToProgress = (status) => {
  switch (status) {
    case 'Open': return 20;
    case 'Pending': return 30;
    case 'Complete': return 70;
    case 'Resolved': return 90;
    case 'Outgoing': return 100;
    default: return 0;
  }
};

const calculateAverageProgress = (tickets) => {
  if (!tickets.length) return 0;
  return tickets.reduce((acc, t) => acc + statusToProgress(t.status), 0) / tickets.length;
};

const getStatusBg = (status) => {
  switch (status) {
    case 'Open': return 'bg-red-500/20 text-red-300 border border-red-500/30';
    case 'Pending': return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
    case 'Complete': return 'bg-green-500/20 text-green-300 border border-green-500/30';
    case 'Resolved': return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
    case 'Outgoing': return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
    default: return 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
  }
};

const ProgressBar = ({ progress, height = 'h-2' }) => {
  const barColor = progress === 100 ? 'bg-green-500' : 'bg-cyan-400';
  return (
    <div className={`w-full bg-slate-700 rounded-full ${height} overflow-hidden`}>
      <div
        className={`${height} ${barColor} rounded-full transition-all duration-700 ease-out`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: '#E0E0E0', font: { size: 12 } } },
    tooltip: {
      backgroundColor: 'rgb(30, 41, 59)',
      titleColor: '#00FFFF',
      bodyColor: '#E0E0E0',
    },
  },
  scales: {
    x: { grid: { color: '#1e293b' }, ticks: { color: '#94a3b8' } },
    y: { grid: { color: '#1e293b' }, ticks: { color: '#94a3b8', stepSize: 1 } },
  },
};

const Workload = () => {
  const [admins, setAdmins] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animatedCounts, setAnimatedCounts] = useState({});
  const [profilePictures, setProfilePictures] = useState({});
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBarChart, setShowBarChart] = useState(false);
  const [showPieChart, setShowPieChart] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const apiUrl = config.API_BASE_URL;

  const animateCount = (username, count) => {
    setAnimatedCounts((prev) => ({ ...prev, [username]: 0 }));
    let counter = 0;
    const interval = setInterval(() => {
      if (counter < count) {
        counter++;
        setAnimatedCounts((prev) => ({ ...prev, [username]: counter }));
      } else clearInterval(interval);
    }, 50);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [adminRes, ticketRes] = await Promise.all([
          axios.get(`${apiUrl}/Admin`),
          axios.get(`${apiUrl}/Tickets`),
        ]);
        setAdmins(adminRes.data);
        setTickets(ticketRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl]);

  useEffect(() => {
    if (!admins.length) return;
    const fetchPics = async () => {
      const pics = await Promise.all(
        admins.map(async (admin) => {
          try {
            const res = await axios.get(`${apiUrl}/Admin/get-profile-picture/${admin.username}`, { responseType: 'blob' });
            return { username: admin.username, imageUrl: URL.createObjectURL(res.data) };
          } catch {
            return { username: admin.username, imageUrl: defaultProfilePicture };
          }
        })
      );
      const obj = {};
      pics.forEach((p) => (obj[p.username] = p.imageUrl));
      setProfilePictures(obj);
    };
    fetchPics();
  }, [admins, apiUrl]);

  useEffect(() => {
    admins.forEach((admin) => {
      const count = tickets.filter((t) => t.assignees === admin.username).length;
      if (count > 0) animateCount(admin.username, count);
    });
  }, [admins, tickets]);

  const getTicketStatusCounts = (username = null) => {
    const filtered = username ? tickets.filter((t) => t.assignees === username) : tickets;
    const counts = { Open: 0, Pending: 0, Complete: 0, Resolved: 0, Outgoing: 0 };
    filtered.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
    return counts;
  };

  const statusCounts = getTicketStatusCounts();

  const barData = {
    labels: admins.map((a) => a.username),
    datasets: [{
      label: 'Ticket Count',
      data: admins.map((a) => tickets.filter((t) => t.assignees === a.username).length),
      backgroundColor: 'rgba(0,255,255,0.65)',
      borderColor: COLORS.cyan,
      borderWidth: 1,
      borderRadius: 6,
    }],
  };

  const pieData = {
    labels: ['Open', 'Pending', 'Complete', 'Resolved', 'Outgoing'],
    datasets: [{
      data: [statusCounts.Open, statusCounts.Pending, statusCounts.Complete, statusCounts.Resolved, statusCounts.Outgoing],
      backgroundColor: [COLORS.red, COLORS.yellow, COLORS.green, COLORS.blue, COLORS.purple],
      hoverOffset: 8,
    }],
  };

  const filteredAdmins = admins.filter((a) => {
    const matchName = a.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = roleFilter ? a.role.toLowerCase() === roleFilter.toLowerCase() : true;
    return matchName && matchRole;
  });

  const handleRowClick = (admin) => {
    setSelectedAdmin(admin);
    setShowModal(true);
    setShowBarChart(false);
    setShowPieChart(false);
  };

  const handleChartToggle = (type) => {
    setSelectedAdmin(ALL_AGENTS_SUMMARY);
    setShowModal(true);
    if (type === 'bar') { setShowBarChart(true); setShowPieChart(false); }
    else { setShowPieChart(true); setShowBarChart(false); }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setShowBarChart(false);
    setShowPieChart(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-cyan-400 font-semibold text-lg tracking-widest uppercase">Loading Workload Data...</p>
        </div>
      </div>
    );
  }

  const renderModalContent = () => {
    if (!selectedAdmin) return null;

    if (selectedAdmin === ALL_AGENTS_SUMMARY) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-2xl font-bold text-white">Overall Dashboard Summary</h2>
              <p className="text-slate-400 text-sm mt-1">All agents · Ticket overview</p>
            </div>
            <button
              onClick={handleCloseModal}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-700 hover:bg-red-500/30 text-slate-300 hover:text-red-400 transition-all text-xl font-bold"
            >
              ×
            </button>
          </div>
          {showBarChart && (
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-700">
              <h4 className="text-cyan-400 font-semibold text-sm uppercase tracking-wider mb-4">Ticket Distribution per Agent</h4>
              <div className="h-72"><Bar data={barData} options={chartOptions} /></div>
            </div>
          )}
          {showPieChart && (
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-700">
              <h4 className="text-cyan-400 font-semibold text-sm uppercase tracking-wider mb-4">Overall Ticket Status Breakdown</h4>
              <div className="h-72"><Pie data={pieData} options={{ ...chartOptions, scales: {} }} /></div>
            </div>
          )}
        </div>
      );
    }

    const assignedTickets = tickets.filter((t) => t.assignees === selectedAdmin.username);
    const adminStatusCounts = getTicketStatusCounts(selectedAdmin.username);
    const totalTickets = assignedTickets.length;
    const averageProgress = calculateAverageProgress(assignedTickets);

    const adminPieData = {
      labels: ['Open', 'Pending', 'Complete', 'Resolved', 'Outgoing'],
      datasets: [{
        data: [adminStatusCounts.Open, adminStatusCounts.Pending, adminStatusCounts.Complete, adminStatusCounts.Resolved, adminStatusCounts.Outgoing],
        backgroundColor: [COLORS.red, COLORS.yellow, COLORS.green, COLORS.blue, COLORS.purple],
        hoverOffset: 8,
      }],
    };

    const adminBarData = {
      labels: ['Open', 'Pending', 'Complete', 'Resolved', 'Outgoing'],
      datasets: [{
        label: 'Count',
        data: [adminStatusCounts.Open, adminStatusCounts.Pending, adminStatusCounts.Complete, adminStatusCounts.Resolved, adminStatusCounts.Outgoing],
        backgroundColor: [COLORS.red, COLORS.yellow, COLORS.green, COLORS.blue, COLORS.purple],
        borderRadius: 6,
      }],
    };

    return (
      <div className="space-y-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <img
                src={profilePictures[selectedAdmin.username] || defaultProfilePicture}
                alt={selectedAdmin.username}
                className="w-16 h-16 rounded-full object-cover border-2 border-green-400 shadow-lg shadow-green-500/20"
              />
              <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-400 rounded-full border-2 border-slate-800" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{selectedAdmin.username}</h2>
              <span className="text-xs font-semibold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 rounded-full uppercase tracking-wide">
                {selectedAdmin.role}
              </span>
            </div>
          </div>
          <button
            onClick={handleCloseModal}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-700 hover:bg-red-500/30 text-slate-300 hover:text-red-400 transition-all text-xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Tickets', value: totalTickets, color: 'text-yellow-400' },
            { label: 'Avg. Progress', value: `${Math.round(averageProgress)}%`, color: 'text-cyan-400' },
            { label: 'Completed', value: adminStatusCounts.Outgoing || 0, color: 'text-green-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-slate-900 rounded-xl p-4 border border-slate-700 text-center">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-slate-400 text-xs mt-1 uppercase tracking-wide">{label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
            <h4 className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-3">Status Bar Chart</h4>
            <div className="h-48"><Bar data={adminBarData} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: false } } }} /></div>
          </div>
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
            <h4 className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-3">Status Breakdown</h4>
            <div className="h-48"><Pie data={adminPieData} options={{ ...chartOptions, scales: {} }} /></div>
          </div>
        </div>

        <div>
          <h4 className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-3">Assigned Tickets</h4>
          <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-y-auto max-h-64">
            {assignedTickets.length === 0 ? (
              <p className="text-center text-yellow-400 py-8 text-sm">No tickets currently assigned.</p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {assignedTickets.map((ticket) => {
                  const progress = statusToProgress(ticket.status);
                  return (
                    <li key={ticket.id} className="p-4 hover:bg-slate-800/50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-white font-medium text-sm">{ticket.title}</span>
                          <span className="text-slate-500 text-xs ml-2">#{ticket.ticketNumber}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusBg(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ProgressBar progress={progress} />
                        <span className="text-slate-400 text-xs w-8 text-right">{progress}%</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 px-6 py-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>

      <div className="max-w-7xl mx-auto space-y-8">

        <div className="flex items-center gap-4">
          <div className="w-1 h-10 bg-cyan-400 rounded-full shadow-[0_0_12px_rgba(0,255,255,0.6)]" />
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">Workload Management</h1>
            <p className="text-slate-400 text-sm mt-0.5">{admins.length} agents · {tickets.length} total tickets</p>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search agent name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 text-sm transition-all"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 text-sm transition-all min-w-40"
          >
            <option value="">All Roles</option>
            <option value="Admin">Admin</option>
            <option value="Manager">Team Manager</option>
            <option value="Support">Support Agent</option>
          </select>

          <button
            onClick={() => handleChartToggle('bar')}
            className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all border ${
              showModal && selectedAdmin === ALL_AGENTS_SUMMARY && showBarChart
                ? 'bg-cyan-400 text-slate-900 border-cyan-400 shadow-[0_0_15px_rgba(0,255,255,0.4)]'
                : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-cyan-500/50 hover:text-cyan-300'
            }`}
          >
            {showModal && selectedAdmin === ALL_AGENTS_SUMMARY && showBarChart ? '✕ Hide Bar Chart' : '📊 Ticket Distribution'}
          </button>
          <button
            onClick={() => handleChartToggle('pie')}
            className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all border ${
              showModal && selectedAdmin === ALL_AGENTS_SUMMARY && showPieChart
                ? 'bg-cyan-400 text-slate-900 border-cyan-400 shadow-[0_0_15px_rgba(0,255,255,0.4)]'
                : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-cyan-500/50 hover:text-cyan-300'
            }`}
          >
            {showModal && selectedAdmin === ALL_AGENTS_SUMMARY && showPieChart ? '✕ Hide Pie Chart' : '🥧 Status Breakdown'}
          </button>
        </div>

        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden shadow-xl">
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-900 border-b border-slate-700">
                <tr>
                  {['Agent', 'Role', 'Active Tickets', 'Avg. Progress'].map((h) => (
                    <th key={h} className="px-5 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredAdmins.map((admin) => {
                  const assigned = tickets.filter((t) => t.assignees === admin.username);
                  const totalTickets = assigned.length;
                  const avgProgress = calculateAverageProgress(assigned);
                  const animated = animatedCounts[admin.username] || 0;
                  const pic = profilePictures[admin.username] || defaultProfilePicture;
                  const countColor = totalTickets > 7 ? 'text-red-400' : totalTickets > 3 ? 'text-yellow-400' : 'text-green-400';

                  return (
                    <tr
                      key={admin.username}
                      onClick={() => handleRowClick(admin)}
                      className="hover:bg-slate-700/40 cursor-pointer transition-colors group"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            <img src={pic} alt={admin.username} className="w-10 h-10 rounded-full object-cover border-2 border-slate-600 group-hover:border-cyan-500/60 transition-all" />
                          </div>
                          <span className="font-semibold text-white">{admin.username}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2.5 py-1 rounded-full">
                          {admin.role}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xl font-bold tabular-nums ${countColor}`}>{animated}</span>
                        <span className="text-slate-500 text-xs ml-1">tickets</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 max-w-32">
                            <ProgressBar progress={Math.round(avgProgress)} />
                          </div>
                          <span className="text-slate-300 text-sm font-medium w-10">{Math.round(avgProgress)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredAdmins.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-12 text-center text-slate-500">No agents found matching your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && selectedAdmin && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
        >
          <div
            className={`bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl shadow-cyan-500/10 w-full overflow-y-auto p-6 ${
              selectedAdmin === ALL_AGENTS_SUMMARY ? 'max-w-2xl' : 'max-w-4xl'
            } max-h-[90vh]`}
          >
            {renderModalContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Workload;